import { useEffect, useRef, useState, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import StructureAPI, {
  extractStructureErrorMessage,
} from "../../../store/reducers/builderReducers/structure/structureApi";
import {
  loadTemplates,
  syncHeaderFooterTemplatesFromStructure,
} from "../../../store/reducers/builderReducers/templates/templateApi";
import WebsiteApi from "../../../store/reducers/builderReducers/website/websiteApi";
import { NotificationManager } from "react-notifications";

import { useAppSelector } from "../../../store/hooks";
import React from "react";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import { StructureMap } from "../../../store/reducers/builderReducers/structure/structureReducer";
import { updateEditorState } from "../../../store/reducers/builderReducers/editor/editorActions";
import {
  logErrorToBackend,
} from "../../_default/utilities/helpers";
import { Edit, ChevronRight } from "@mui/icons-material";
import { SidebarMenus } from "../Sidebar/Sidebar";
import {
  Page,
  Pages as PagesStore,
} from "../../../store/reducers/builderReducers/pages/pagesApi";
import { setActivePage } from "../../../store/reducers/builderReducers/pages/pagesActions";
import { getLegacyWebpageIdForStructure } from "../../../store/reducers/builderReducers/pages/pageIdentity";
import { Language } from "../../../store/reducers/builderReducers/languageSettings/languageSettingsApi";
import {
  findActiveLanguageByCode,
  languageCodeFromActiveLanguage,
  translationTargetUuid,
  translationTargetWebpageId,
} from "../../../utils/languageCode";
import { resolveLanguageFlagIconUrl } from "../../_default/icons/languageFlagIcons";

interface Props {
  sidebarActive: boolean;
  toggleSidebar: (val: boolean) => void;
  setSelectedMenu: React.Dispatch<React.SetStateAction<SidebarMenus>>;
  setEditPageId: React.Dispatch<React.SetStateAction<number | null>>;
}

export default function Header({
  sidebarActive,
  toggleSidebar,
  setSelectedMenu,
  setEditPageId,
}: Props) {
  const activePageUuid = useAppSelector((state) => state.builder.pages.active);
  const structure = useAppSelector((state) => state.builder.structure);
  const website = useAppSelector((state) => state.builder.website);
  const pages = useAppSelector(
    (state) => state.builder.pages.get("list") as PagesStore["list"]
  );
  const structureWebpageNumericId = useMemo(
    () => getLegacyWebpageIdForStructure(pages, activePageUuid),
    [pages, activePageUuid]
  );
  const languages = useAppSelector((state) => state.builder.languageSettings);

  const initialStructureRef = useRef<StructureMap | null>(null);
  const previousActivePage = useRef<string | null>(activePageUuid);
  const [dataFetched, setDataFetched] = useState(false);
  const [pageChanged, setPageChanged] = useState(false);
  const [structureInitialized, setStructureInitialized] = useState(false);

  const [loadingStructure, setIsLoadingStructure] = useState(false);

  // Get the current page object
  const currentPage = useMemo(() => {
    if (!activePageUuid || !pages) return null;
    return pages.find((page) => page.uuid === activePageUuid) || null;
  }, [activePageUuid, pages]);

  // Build breadcrumb path (from root to current page)
  const breadcrumbPath = useMemo(() => {
    if (!currentPage || !pages) return [];

    const path: Page[] = [];
    let page: Page | undefined = currentPage;

    // Traverse up through parents
    while (page) {
      path.unshift(page);
      if (page.parentWebpageId) {
        page = pages.find((p) => p.id === page!.parentWebpageId);
      } else {
        page = undefined;
      }
    }

    return path;
  }, [currentPage, pages]);

  // Get language translations of the current page
  const pageLanguageVersions = useMemo(() => {
    if (!currentPage || !pages || !languages?.activeLanguages) return [];

    const versions: { page: Page; language: Language }[] = [];
    const seenPageKeys = new Set<string>();

    const pageKey = (p: Page) => (p.uuid || "").trim() || String(p.id);

    const addVersion = (page: Page, language: Language | undefined) => {
      if (!language) return;
      const key = pageKey(page);
      if (!key || seenPageKeys.has(key)) return;
      seenPageKeys.add(key);
      versions.push({ page, language });
    };

    // Add the current page
    const currentLang = findActiveLanguageByCode<Language>(
      languages.activeLanguages,
      currentPage.languageCode
    );
    addVersion(currentPage, currentLang);

    // Add translation pages
    if (currentPage.translationWebpages) {
      currentPage.translationWebpages.forEach((translation) => {
        const targetUuid = translationTargetUuid(translation);
        const targetId = translationTargetWebpageId(translation);
        const translatedPage = pages.find((p) => {
          if (targetUuid && p.uuid === targetUuid) {
            return true;
          }
          if (targetId != null && p.id === targetId) {
            return true;
          }
          return false;
        });
        if (translatedPage) {
          const lang = findActiveLanguageByCode<Language>(
            languages.activeLanguages,
            translatedPage.languageCode
          );
          addVersion(translatedPage, lang);
        }
      });
    }

    // Also check if any other page has a translation pointing to this page
    pages.forEach((page) => {
      if (page.id === currentPage.id) return;
      if (page.translationWebpages) {
        const hasTranslation = page.translationWebpages.some((t) => {
          const targetUuid = translationTargetUuid(t);
          const targetId = translationTargetWebpageId(t);
          if (targetUuid && targetUuid === currentPage.uuid) {
            return true;
          }
          if (targetId != null && targetId === currentPage.id) {
            return true;
          }
          return false;
        });
        if (hasTranslation) {
          const lang = findActiveLanguageByCode<Language>(
            languages.activeLanguages,
            page.languageCode
          );
          addVersion(page, lang);
        }
      }
    });

    return versions;
  }, [currentPage, pages, languages]);

  // Handle clicking on a language to switch to that page version
  const handleLanguageClick = (pageUuid: string) => {
    setActivePage(pageUuid);
  };

  // Handle clicking on Edit button
  const handleEditClick = () => {
    if (!sidebarActive) {
      toggleSidebar(true);
    }
    setSelectedMenu("pages");
    if (currentPage) {
      setEditPageId(currentPage.id);
    }
  };

  useEffect(() => {
    if (activePageUuid !== previousActivePage.current) {
      setPageChanged(true); // Page has changed, flag it
      setDataFetched(false);
      setStructureInitialized(false);
      initialStructureRef.current = null;
      previousActivePage.current = activePageUuid;

      // Indicate that we're loading the structure
      setIsLoadingStructure(true);
    }
  }, [activePageUuid]);

  useEffect(() => {
    if (
      !dataFetched &&
      structure &&
      structure.components &&
      Object.keys(structure.components).length >= 0
    ) {
      initialStructureRef.current = cloneDeep(structure);
      setDataFetched(true);
      setIsLoadingStructure(false);
      setStructureInitialized(true);
      if (pageChanged) {
        setPageChanged(false);
      }
    }
  }, [structure, dataFetched, pageChanged]);

  const userPerson = {
    firstname: "Kasutajanimi",
    avatar: "https://d2.itprojektid.ee/assets/images/default_avatar.png",
  };

  const [saving, setSaving] = useState(0);
  const handleSave = () => {
    setSaving(1);

    if (!initialStructureRef.current || !structureInitialized) {
      NotificationManager.error("Unable to save. Please try again", null, 2000);

      const noInitialStructure = {
        name: "initialStructure fail",
        message: "",
        stack:
          "Error in Saveconfirmation.tsx, missing initialStructure.current or structureInitalized ",
        componentStack: location.pathname,
        href: window.location.href,
      };
      logErrorToBackend(noInitialStructure);

      updateEditorState({
        hasEdited: false,
      });
      setSaving(0);
      return;
    }

    if (structureWebpageNumericId == null) {
      NotificationManager.error("Unable to save. No active webpage.", null, 2000);
      setSaving(0);
      return;
    }

    if (initialStructureRef.current && isEqual(initialStructureRef.current, structure)) {
      NotificationManager.info("No changes detected.");
      setSaving(0);
      updateEditorState({
        hasEdited: false,
      });
      return;
    }

    StructureAPI.updateWebpageComponents(structureWebpageNumericId, structure, {
      sourceStructure: structure,
      baselineStructure: initialStructureRef.current ?? undefined,
    })
        .then((savedStructure) => {
          // Keep the local baseline in sync with repaired/saved structure.
          initialStructureRef.current = cloneDeep(savedStructure);

          syncHeaderFooterTemplatesFromStructure(savedStructure);
          void loadTemplates();

          updateEditorState({
            hasEdited: false,
          });
        })
        .catch((er) => {
          NotificationManager.error(
            extractStructureErrorMessage(er) || "An error occurred!",
            null,
            4000
          );
        })
        .finally(() => setSaving(0));
  };

  const [publishing, setPublishing] = useState(0);
  const handlePublish = () => {
    if (structureWebpageNumericId == null) {
      NotificationManager.error("Unable to publish. No active webpage.", null, 2000);
      return;
    }
    setPublishing(1);
    StructureAPI.updateWebpageComponents(structureWebpageNumericId, structure, {
      action: "publish",
    })
      .then((savedStructure) => {
        initialStructureRef.current = cloneDeep(savedStructure);
        syncHeaderFooterTemplatesFromStructure(savedStructure);
        void loadTemplates();
        updateEditorState({
          hasEdited: false,
        });
        return WebsiteApi.publishWebsite();
      })
      .then(() => NotificationManager.success("Website published!", null, 5000))
      .catch((er) => {
        console.log(er);
        NotificationManager.error(
          extractStructureErrorMessage(er) || "An error occurred!",
          null,
          5000
        );
      })
      .finally(() => setPublishing(0));
  };

  return (
    <nav
      className="header  navbar-expand"
      style={{
        position: "sticky",
        top: "0",
        width: "100%",
        zIndex: 0,
      }}
    >
      <div className="navbar-header"></div>
      <div className="collapse navbar-collapse h-100" id="navbar">
        <div className="col d-flex align-items-center">
          <div
            className={"home-btn" + (sidebarActive ? "" : " active")}
            onClick={() => toggleSidebar(!sidebarActive)}
          >
            <HomeIcon />
          </div>
          <label className="website-title b-ps-4 fw-bold">
            {website?.name}
          </label>

          {/* Breadcrumb navigation */}
          <div className="header-breadcrumbs">
            {breadcrumbPath.map((page, index) => (
              <React.Fragment key={page.uuid || String(page.id)}>
                {index > 0 && <ChevronRight className="breadcrumb-separator" />}
                <span
                  className={`breadcrumb-item ${
                    index === breadcrumbPath.length - 1 ? "active" : ""
                  }`}
                  onClick={() => setActivePage(page.uuid)}
                >
                  {page.name}
                </span>
              </React.Fragment>
            ))}
          </div>

          {/* Edit button */}
          <div
            className="header-edit-btn"
            onClick={handleEditClick}
            title="Edit page settings"
          >
            <Edit />
          </div>

          {/* Language flags */}
          <div className="header-languages">
            {pageLanguageVersions.map((version) => {
              const row = version.language;
              const code = languageCodeFromActiveLanguage(row);
              const iconUrl = resolveLanguageFlagIconUrl(
                code,
                row.language?.icon_url?.trim() ?? ""
              );
              if (!iconUrl) {
                return null;
              }
              return (
                <img
                  key={version.page.uuid || String(version.page.id)}
                  className={`header-language-flag ${
                    version.page.uuid === activePageUuid ? "active" : ""
                  }`}
                  onClick={() => handleLanguageClick(version.page.uuid)}
                  height="15px"
                  width="25px"
                  style={{ objectFit: "cover" }}
                  src={iconUrl}
                  alt={code}
                  title={`Switch to ${code} version`}
                />
              );
            })}
          </div>
        </div>

        {/* <HeaderResponsiveOptions /> */}
        <div className="col h-100 d-flex align-items-center justify-content-end">
          <button
            className="b-btn btn-primary h-100 rounded-0 fw-bold saving-btn"
            style={{ width: "100px" }}
            onClick={() => {
              handleSave();
            }}
            disabled={saving === 1 || publishing === 1 || loadingStructure}
          >
            Save
            {saving === 1 && (
              <div
                className="loader loader-circle"
                style={{ verticalAlign: "text-top", fontSize: "inherit" }}
              ></div>
            )}
          </button>
          <button
            className="b-btn h-100 rounded-0 fw-bold publish-btn"
            style={{ width: "100px", color: "#fff" }}
            onClick={handlePublish}
            disabled={publishing === 1 || saving === 1}
          >
            Publish
            {publishing === 1 && (
              <div
                className="loader loader-circle"
                style={{ verticalAlign: "text-top", fontSize: "inherit" }}
              ></div>
            )}
          </button>
          <div className="b-ms-4 b-me-5 d-flex align-items-center user-container">
            <div className="position-relative avatar">
              {/*@ts-ignore*/}
              <img
                alt="Avatar"
                src={userPerson.avatar}
                className="b-me-4"
                style={{ height: "30px", width: "30px", borderRadius: "14px" }}
              ></img>
              <i className="avatar-online"></i>
            </div>
            {/*@ts-ignore*/}
            <label>{userPerson.firstname}</label>
          </div>
          <a
            className="b-btn btn-dark h-100 rounded-0"
            style={{ color: "#fff" }}
            href={"/websites/" + website.id}
          >
            <CloseIcon />
          </a>
        </div>
      </div>
    </nav>
  );
}
